import React, { useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GroupSelectPopup from "../GroupSelectPopup";
import './Accounts.css';
import inputStyles from '../../views/ManagementPages.module.css';
import { setFormField } from './RegistrationSlice';
import { Tooltip, OverlayTrigger, Form, FloatingLabel } from "react-bootstrap";
import MultiSelectDropdown from "../MultiSelectDropdown";
import FileUploadOrganization from "../../views/new-organization/add-edit-organization/FileUploadOrganization";
import { SpecialtySelectedIcon, RemoveIcon } from "../icons/Icons";
import { TrashIcon } from "../icons/Icons";

const GroupRoleInfo = ({ selectedOrganization, setSelectedOrganization, errors, setProfileFile }) => {

    const dispatch = useDispatch()
    const groupData = useSelector((state) => state.userInfo.organizationsHierarchy);
    const dropdowns = useSelector((state) => state.dropdowns);
    const [validated, setValidated] = useState(false);

    const userData = useSelector((state) => state.registration);
    const updating = userData.updating;
    const ehrAccount = userData.ehrAccount;
    const filteredGroupData = ehrAccount 
        ? groupData
        .map(org => ({
            ...org,
            groups: org.groups.filter(group => group.inpatient === true)
        }))
        .filter(org => org.groups && org.groups.length > 0)
        : userData?.role?.organizationId
            ? groupData.filter((org) => org.organizationId === userData.role.organizationId)
            : groupData;

 
    const roleCategory = userData.role.category;
    const [selectedSpecialties, setSelectedSpecialties] = useState(userData.specialties ?? []);
    const [isGroupSelectVisible, setIsGroupSelectVisible] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const userGroups = userData.groups;
    const signedInUserOrganizationData = useSelector((state) => state.userInfo.organizationsHierarchy);
    const filteredSignedInUserOrganizationData = ehrAccount 
    ? signedInUserOrganizationData
    .filter(org => Number(org.organizationId) === userData.ehrOrgId)
        .map(org => ({
            ...org,
            groups: org.groups.filter(group => group.inpatient === true)
        }))
        .filter(org => org.groups && org.groups.length > 0)
    : userData?.role?.organizationId
        ? signedInUserOrganizationData.filter((org) => org.organizationId === userData.role.organizationId)
        : signedInUserOrganizationData;
   
    const initEmployeeInfo = (roleCategory === "Doctor" || userData.npi !== "") ? "npi" : "employeeId";
    const [employeeInfo, setEmployeeInfo] = useState(initEmployeeInfo);

    const groupsErrorShowing = errors.groupsShowing && userGroups.length === 0;

    const AppointmentsTooltip = (
        <Tooltip id="tooltip">
            This enables staff, like nurse practitioners, to meet with anyone in the organization and have appointments.
        </Tooltip>
    );

    const AvailabilityTooltip = (
        <Tooltip id="tooltip">
            Allows accounts to manage team availability and appointments.
        </Tooltip>
    );

    const toggleGroupSelectVisibility = () => {
        setIsGroupSelectVisible(!isGroupSelectVisible);
    };

    const initGroupList = (orgId, selectingGroups = false) => {
        let tempGroups = [];
        groupData.forEach((org) => {
            if (org.organizationId === orgId) {
                tempGroups = tempGroups.concat(
                    org.groups.map((group) => ({
                        groupId: group.groupId,
                        groupName: group.groupName
                    }))
                );
            }
        });
        if (selectingGroups) {
            tempGroups = tempGroups.filter(group => {
                return userData.groups.some(userGroup => userGroup.groupId === group.groupId);
            });
        }
        return tempGroups;
    };

    const [groupsDisplay, setGroupsDisplay] = useState(initGroupList(selectedOrganization));
    const [selectedGroups, setSelectedGroups] = useState(initGroupList(selectedOrganization, true));

    const initSpecialities = () => {
        let tempSpecialties = dropdowns.specialty.values.map((s) => ({
            id: s.id, name: s.description
        }));
        return tempSpecialties;
    };

    const specialtiesList = initSpecialities();

    const [primarySpecialty, setPrimarySpecialty] = useState(() => {
        if (userData.specialties) {
            const primaryCategory = selectedSpecialties.find(item => item.isPrimary);
            return primaryCategory ? primaryCategory.id : "";
        } else {
            return "";
        }
    });

    const checkSelectedSpecialty = (value) => {
        return selectedSpecialties.some((item) => item.id === value);
    };

    const handlePrimaryClick = (specialty) => {
        setPrimarySpecialty(specialty.id);

        const updatedSpecialties = selectedSpecialties.map(option => {
            if (option.id === specialty.id) {
                return { ...option, isPrimary: true };
            } else {
                return { ...option, isPrimary: false };
            }
        });
        setSelectedSpecialties(updatedSpecialties);
        dispatch(setFormField({ key: "specialties", value: updatedSpecialties.map(s => ({ id: s.id, isPrimary: s.isPrimary })) }));
    };

    const handleSpecialtiesChange = (selectedSpecialty) => {
        let inSpecialtiesList = selectedSpecialties.some((item) => item.id === selectedSpecialty.id);
        let updatedSpecialties;
        if (inSpecialtiesList) {
            updatedSpecialties = selectedSpecialties.filter(item => item.id !== selectedSpecialty.id);
            setSelectedSpecialties(updatedSpecialties);
        } else {
            const newSpecialty = { id: selectedSpecialty.id, name: selectedSpecialty.name, isPrimary: selectedSpecialty.id === primarySpecialty };
            setSelectedSpecialties([...selectedSpecialties, newSpecialty]);
            updatedSpecialties = [...selectedSpecialties, newSpecialty];
        }
        dispatch(setFormField({ key: "specialties", value: updatedSpecialties }))
    };

    const summarizeGroups = () => {
        if (userGroups && userGroups.length >= 1 && userGroups[0].hasOwnProperty("subGroupId") && groupData.length >= 1) {
            return userGroups.map(({ organizationId, groupId, subGroupId, teamId }) => {
                const organization = groupData.find(org => org.organizationId === organizationId);
                if (!organization) return null;
                const group = organization.groups.find(grp => grp.groupId === groupId);
                if (!group) return null;
                const subgroup = group.subgroups.find(sub => sub.subGroupId === subGroupId);
                if (!subgroup) return null;
                const team = subgroup.teams.find(t => t.teamId === teamId);
                if (!team) return null;
                return {
                    organizationName: organization.organizationName,
                    groupName: group.groupName,
                    subGroupName: subgroup.subGroupName,
                    teamName: team.teamName,
                    teamId: teamId,
                };
            }).filter(Boolean)
                .sort((a, b) => {
                    if (a.organizationName !== b.organizationName) {
                        return a.organizationName.localeCompare(b.organizationName);
                    }
                    if (a.subGroupName !== b.subGroupName) {
                        return a.subGroupName.localeCompare(b.subGroupName);
                    }
                    return a.teamName.localeCompare(b.teamName);
                });
        } else {
            return [];
        }
    };
    const groupSummary = summarizeGroups();

    const handleOrganization = (e) => {
        setSelectedOrganization(Number(e.target.value));
        let groupsList = initGroupList(Number(e.target.value));
        dispatch(setFormField({ key: "groups", value: [] }));
        setGroupsDisplay(groupsList);
        setSelectedGroups([]);
    };

    const removeOrganization = (teamId) => {
        const newGroups = userGroups.filter((team) => team.teamId !== teamId);
        dispatch(setFormField({ key: "groups", value: newGroups }));
    };

    const checkSelectedItem = (groupId) => {
        let groupInList = false;
        selectedGroups.forEach((item) => {
            if (item.groupId === groupId) {
                groupInList = true;
            }
        });
        return groupInList;
    };

    const handleGroupChange = (group) => {
        let groupInList = checkSelectedItem(group.groupId);
        let newGroups = [];
        if (groupInList) {
            // remove from selectedGroups and from userData 
            setSelectedGroups(selectedGroups.filter(item => item.groupId !== group.groupId));
            newGroups = userGroups.filter(item => item.groupId !== group.groupId);
        } else {
            setSelectedGroups([group, ...selectedGroups]);
            if (userGroups.length === 1 && userGroups[0].groupId === 0) {
                newGroups = [{ groupId: group.groupId, organizationId: selectedOrganization }];
            } else {
                newGroups = [{ groupId: group.groupId, organizationId: selectedOrganization }, ...userGroups];
            }
        }
        dispatch(setFormField({ key: "groups", value: newGroups }));
    };

    const dispatchGroups = (groups) => {
        dispatch(setFormField({ key: "groups", value: groups }));
    };

    const handleIDChange = (e) => {
        if (e.target.id === "employeeId") {
            setSelectedSpecialties([]);
            setPrimarySpecialty("");
            dispatch(setFormField({ key: "specialties", value: [] }));
            dispatch(setFormField({ key: "npi", value: '' }));
        } else {
            dispatch(setFormField({ key: "employeeId", value: '' }));
        }
        setEmployeeInfo(e.target.value);
    };

    return (
        <div className="create-account-inputs">
            <div className="input-name">
                Group Info
            </div>
            {roleCategory !== ("Admin") &&
                <button type="button" style={groupsErrorShowing ? { border: "1px solid red" } : {}} className="group-button" onClick={toggleGroupSelectVisibility}>
                    Assign
                </button>
            }
            {groupsErrorShowing &&
                <div className="page-error">
                    Please assign to a group.
                </div>
            }
            {isGroupSelectVisible &&
                <GroupSelectPopup
                    initialSelections={userGroups}
                    organizationData={filteredSignedInUserOrganizationData}
                    handleSave={dispatchGroups}
                    onClose={toggleGroupSelectVisibility}
                />
            }
            {roleCategory !== ("Admin") &&
                <div>
                    <ul className="groups-list">
                        {groupSummary.map((group) => (
                            <li
                                key={group.teamId}
                                className="group-list-item"
                            >
                                {group.groupName} - {group.subGroupName} - {group.teamName}
                                <div style={{ cursor: "pointer" }} onClick={() => removeOrganization(group.teamId)}>
                                    <TrashIcon />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            }
            {roleCategory === ("Admin") &&
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Organization</span>
                        <select
                            className={inputStyles["styled-input-select"]}
                            id="groupId"
                            value={selectedOrganization}
                            style={groupsErrorShowing ? { border: "1px solid red", width: "300px" } : { width: "300px" }}
                            onChange={handleOrganization}
                            disabled={updating}
                        >
                            <option value={0}>Select an Organization</option>
                            {filteredGroupData.map((org) => (
                                <option key={org.organizationId} id={org.organizationName} value={org.organizationId}>{org.organizationName}</option>
                            ))}
                        </select>
                    </div>
                    {selectedOrganization !== 0 &&
                        <MultiSelectDropdown
                            options={groupsDisplay}
                            label='groupName'
                            checkedKey='groupId'
                            checkSelectedItem={checkSelectedItem}
                            handleChange={handleGroupChange}
                            selectedItems={selectedGroups}
                            name='Groups'
                            error={groupsErrorShowing}
                        />
                    }
                </div>
            }
            <div className="input-name">
                Role Info
            </div>
            {roleCategory !== "Patient" &&
                <div>
                    <div style={{ display: "flex", marginBottom: "10px", columnGap: "10px" }}>
                        <div>
                            <input
                                id="npi"
                                type="radio"
                                value="npi"
                                checked={employeeInfo === "npi"}
                                onChange={handleIDChange}
                            />
                            <span style={{ paddingLeft: "5px" }}>NPI Number</span>
                        </div>
                        <div>
                            <input
                                id="employeeId"
                                type="radio"
                                value="employeeId"
                                checked={employeeInfo === "employeeId"}
                                onChange={handleIDChange}
                            />

                            <span style={{ paddingLeft: "5px" }}>Employee ID</span>
                        </div>
                    </div>
                    {employeeInfo === "npi" ?
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <MultiSelectDropdown
                                options={specialtiesList}
                                label='name'
                                checkedKey='id'
                                checkSelectedItem={checkSelectedSpecialty}
                                handleChange={handleSpecialtiesChange}
                                selectedItems={selectedSpecialties}
                                name='Specialties'
                            />
                            {selectedSpecialties.length >= 1 && selectedSpecialties[0].id !== '' &&
                                <div className="input-name">
                                    <span>Select Primary Specialty</span>
                                    <ul className="primary-specialties">
                                        {selectedSpecialties.map((specialty) => (
                                            <li
                                                key={specialty.id}
                                                className={primarySpecialty === specialty.id ? 'li-specialty' : 'li-primary'}
                                            >
                                                <div style={{ alignItems: "center", display: "flex", columnGap: "10px" }} onClick={() => handlePrimaryClick(specialty)}>
                                                    {primarySpecialty === specialty.id &&
                                                        <SpecialtySelectedIcon />
                                                    }
                                                    {specialty.name}
                                                </div>
                                                <div style={{ alignSelf: "center", display: "flex" }} onClick={() => handleSpecialtiesChange(specialty)}>
                                                    <RemoveIcon />
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            }
                            <Form.Group controlId="npi">
                                <FloatingLabel label="NPI*">
                                    <Form.Control
                                        value={userData.npi}
                                        className={`${inputStyles.BorderedComponent}`}
                                        onChange={(e) => dispatch(setFormField({ key: "npi", value: e.target.value }))}
                                        placeholder="NPI*"
                                        style={{ width: "300px" }}
                                        required
                                        type="text"
                                        pattern="^\d{10}$"
                                        isInvalid={
                                            validated &&
                                            !/^\d{10}$/.test(userData.npi)
                                        }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please provide a valid NPI; NPIs must contain only numbers and be 10 digits in length.
                                    </Form.Control.Feedback>
                                </FloatingLabel>

                            </Form.Group>
                        </div>
                        :
                        <Form.Group controlId="employeeId">
                            <FloatingLabel label="Employee Id*">
                                <Form.Control
                                    value={userData.employeeId}
                                    className={`${inputStyles.BorderedComponent}`}
                                    onChange={(e) => dispatch(setFormField({ key: "employeeId", value: e.target.value }))}
                                    placeholder="Employee Id*"
                                    style={{ width: "300px" }}
                                    required
                                    type="text"
                                    isInvalid={validated && userData.employeeId.trim() === ""}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Please provide a valid Employee Id.
                                </Form.Control.Feedback>
                            </FloatingLabel>

                        </Form.Group>
                    }
                    <Form.Group style={{ marginTop: "10px" }} controlId="staffTitle">
                        <FloatingLabel label="Staff Title">
                            <Form.Select
                                type="select"
                                value={userData.title ?? ""}
                                className={`${inputStyles.BorderedComponent}`}
                                onChange={(e) => dispatch(setFormField({ key: "title", value: e.target.value }))}
                                placeholder="Staff Title"
                                style={{ width: "300px" }}
                            >
                                <option value="">Select a Title</option>
                                {dropdowns.staffTitles.values.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.display}
                                    </option>
                                ))}
                            </Form.Select>
                        </FloatingLabel>
                    </Form.Group>
                </div>
            }
            {roleCategory === "Patient" &&
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div className={inputStyles["styled-input-container"]}>
                        <input
                            id="mrn"
                            defaultValue={userData.mrn}
                            style={{ width: "300px" }}
                            onChange={(e) => dispatch(setFormField({ key: "mrn", value: e.target.value }))}
                        ></input>
                        <label className={userData.mrn && inputStyles['filled']}>
                            Medical Record Number
                        </label>
                    </div>
                    <div className={inputStyles["styled-input-container"]} style={{ width: "300px" }}>
                        <input
                            type={
                                showPassword ? "text" : "password"
                            }
                            maxLength="9"
                            id="ssn"
                            style={{ width: "300px" }}
                            defaultValue={userData.ssn}
                            onChange={(e) => dispatch(setFormField({ key: "ssn", value: e.target.value }))}
                        ></input>
                        <label className={userData.ssn && inputStyles['filled']}>
                            Social Security Number
                        </label>
                        <div className={inputStyles["styled-icon-container"]} onClick={() =>
                            setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <FontAwesomeIcon icon="fa-regular fa-eye" className="eye-icon" size="lg" />
                                : <FontAwesomeIcon icon="fa-regular fa-eye-slash" className="eye-icon" size="lg" />}
                        </div>
                    </div>
                </div>
            }
            {roleCategory !== "Patient" &&
                <>
                    <div style={{ display: "flex", columnGap: "5px", alignItems: "center", marginTop: "10px" }}>
                        <Form.Check
                            style={{ paddingBottom: "2px" }}
                            type="switch"
                            id="custom-switch"
                            checked={userData.canHaveAppointment}
                            onChange={(e) => dispatch(setFormField({ key: "canHaveAppointment", value: e.target.checked }))}
                        />
                        <div className="input-name">Appointment Eligible</div>
                        <OverlayTrigger placement="right" overlay={AppointmentsTooltip}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ color: '#686869' }} />
                        </OverlayTrigger>
                    </div>
                    <div style={{ display: "flex", columnGap: "5px", alignItems: "center", marginTop: "10px" }}>
                        <Form.Check
                            style={{ paddingBottom: "2px" }}
                            type="switch"
                            id="custom-switch"
                            checked={userData.canManageAvailability}
                            onChange={(e) => dispatch(setFormField({ key: "canManageAvailability", value: e.target.checked }))}
                        />
                        <div className="input-name">Manage Availability</div>
                        <OverlayTrigger placement="right" overlay={AvailabilityTooltip}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ color: '#686869' }} />
                        </OverlayTrigger>
                    </div>
                </>
            }
            <div style={{ marginTop: "20px" }} className="input-name">
                Profile Photo
            </div>
            <div style={{ width: "300px" }}>
                <FileUploadOrganization
                    fileType='image'
                    onDrop={setProfileFile}
                    imageURL={userData.profilePictureURL}
                    acceptedTypes={"PNG,JPG | 8MB Max"}
                />
            </div>
        </div>
    );
};

export default GroupRoleInfo; 
